'use client';

import React, { useCallback, useMemo, useState } from 'react';

import { getNodesByKeys, TreeCommand, TreeNodeData } from 'src/components/tree';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';

import { SelectValues } from './select-values';
import { SelectLoading } from './SelectLoading';
import { SelectOption } from './types';

export interface TreeSelectProps<TData = unknown, TMultiple extends boolean = false> {
  // 数据相关
  data: TreeNodeData<TData>[];
  multiple?: TMultiple;

  // TreeSelect 特有的属性
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  loading?: boolean;
  loadingText?: string;
  maxTagCount?: TMultiple extends true ? number : never;
  tagRender?: TMultiple extends true
    ? (node: TreeNodeData<TData>, onRemove: () => void) => React.ReactNode
    : never;

  // 显示相关
  getDisplayValue?: (node: TreeNodeData<TData>) => string;

  // 选择相关
  value?: TMultiple extends true ? string[] : string | null;
  onChange?: (value: TMultiple extends true ? string[] : string | null) => void;

  // 事件
  onClear?: () => void;
  onDropdownVisibleChange?: (visible: boolean) => void;

  // TreeCommand 的其他属性
  defaultExpandAll?: boolean;
  defaultExpandedKeys?: string[];
  filterTreeNode?: (node: TreeNodeData<TData>, searchValue: string) => boolean;
  searchPlaceholder?: string;
  onNodeClick?: (node: TreeNodeData<TData>, event: React.MouseEvent) => void;
  /** 判断节点是否可选，返回 false 时点击节点将展开/收起而非选中 */
  isNodeSelectable?: (node: TreeNodeData<TData>) => boolean;
}

export function TreeSelect<TData = unknown, TMultiple extends boolean = false>({
  // TreeSelect 属性
  placeholder = '请选择',
  className,
  disabled = false,
  clearable = true,
  loading = false,
  loadingText = '加载中...',
  tagRender,
  getDisplayValue,
  onClear,
  onDropdownVisibleChange,

  // 选择相关
  value,
  onChange,
  multiple,
  onNodeClick,

  // TreeCommand 属性
  data,
  ...treeCommandProps
}: TreeSelectProps<TData, TMultiple>) {
  const [open, setOpen] = useState(false);

  // 获取选中的节点
  const selectedOptions = useMemo(() => {
    const selectedKeys = multiple ? (value as string[]) || [] : value ? [value as string] : [];
    const nodes = getNodesByKeys(data, selectedKeys);
    return nodes.map((node) => ({
      label: node.name,
      value: node.origin as TData,
      key: node.key,
    }));
  }, [data, value, multiple]);

  // 处理清空
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (multiple) {
        onChange?.([] as any);
      } else {
        onChange?.(null as any);
      }
      onClear?.();
    },
    [multiple, onChange, onClear]
  );

  // 处理移除单个标签
  const handleRemoveTag = useCallback(
    (nodeKey: string) => {
      if (multiple && Array.isArray(value)) {
        const newSelection = value.filter((key: string) => key !== nodeKey);
        onChange?.(newSelection as any);
      }
    },
    [multiple, value, onChange]
  );

  // 处理 popover 开关
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onDropdownVisibleChange?.(newOpen);
    },
    [onDropdownVisibleChange]
  );

  // 处理节点点击（透传给外部回调，不控制关闭）
  const handleNodeClick = useCallback(
    (node: TreeNodeData<TData>, event: React.MouseEvent) => {
      onNodeClick?.(node, event);
    },
    [onNodeClick]
  );

  // 包装 onSelectionChange，在单选模式下选中后关闭
  const handleSelectionChange = useCallback(
    (newValue: TMultiple extends true ? string[] : string | null) => {
      onChange?.(newValue);
      // 单选模式下，只有“选中某项”时关闭（避免取消选中时也关闭）
      if (!multiple && typeof newValue === 'string') {
        setOpen(false);
      }
    },
    [onChange, multiple]
  );

  // 用于 SelectValues 的 displayValue 函数
  const getSelectValuesDisplayValue = useCallback(
    (option: SelectOption<TData>) => {
      if (getDisplayValue) {
        // 从 data 中找到对应的节点
        const nodes = getNodesByKeys(data, [option.key]);
        if (nodes.length > 0) {
          return getDisplayValue(nodes[0]);
        }
      }
      return option.label;
    },
    [getDisplayValue, data]
  );

  // 用于 SelectValues 的 tagRender 函数
  const selectValuesTagRender = useMemo(() => {
    if (!tagRender || !multiple) return undefined;
    return (option: SelectOption<TData>, onRemove: () => void) => {
      const nodes = getNodesByKeys(data, [option.key]);
      if (nodes.length > 0) {
        return tagRender(nodes[0], onRemove);
      }
      return option.label;
    };
  }, [tagRender, multiple, data]);

  // Loading 状态下的处理
  if (loading) {
    return <SelectLoading className={className} loadingText={loadingText} />;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger asChild>
        <SelectValues<TData, TMultiple>
          selectedOptions={selectedOptions}
          multiple={multiple}
          placeholder={placeholder}
          getDisplayValue={getSelectValuesDisplayValue}
          tagRender={selectValuesTagRender as any}
          onRemoveTag={handleRemoveTag}
          disabled={disabled}
          clearable={clearable}
          onClear={handleClear}
          className={className}
          aria-controls="tree-select-content"
          aria-expanded={open}
        />
      </PopoverTrigger>

      <PopoverContent
        id="tree-select-content"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <TreeCommand
          {...treeCommandProps}
          data={data}
          multiple={multiple}
          selection={value}
          onSelectionChange={handleSelectionChange}
          onNodeClick={handleNodeClick}
        />
      </PopoverContent>
    </Popover>
  );
}
